import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, NgZone, ChangeDetectorRef } from "@angular/core";
import { Subscription } from 'rxjs';
import { DataService, FireForesightService, SiteTypeService } from "src/api/services";
import { Incident } from "src/api/models";
import { ApplicationConfigService } from "src/service/application-config.service";
import { AzureAuthService } from "src/service/azure-auth.service";
import { FireService } from "src/service/fire.service";
//import { MqttDatabusService } from "src/service/mqtt-databus.service";
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute } from "@angular/router";
import * as L from 'leaflet';
import { CommandCentreMapComponent } from "src/app/components/command-centre-map/command-centre-map.component";
import { SmartImageDetail } from "src/app/components/smart-image-list/smart-image-list.component";

/** A single entry in the raw telemetry stream. */
export interface TelemetryEntry {
  timestamp: Date;
  deviceId: string;
  deviceName: string;
  topic: string;
  payload: string;
}

/** Summary row in the device activity matrix. */
export interface DeviceActivity {
  deviceId: string;    // siteUri
  deviceName: string;
  lastSeen: Date;
  lastSeenLabel: string;
  lastTopic: string;
  offline: boolean;
  stale: boolean;      // true when no message received within STALE_THRESHOLD_MS
}

@Component({
  selector: 'app-fire-foresight-command',
  templateUrl: './fire-foresight-command.component.html',
  styleUrls: ['./fire-foresight-command.component.css']
})
export class FireForesightCommandComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('commandMap') commandMap: CommandCentreMapComponent;
  public leafletMap: L.Map;

  // ── Telemetry feed state ──────────────────────────────────────────────────

  /** Raw telemetry entries, newest first, capped at MAX_LOG. */
  telemetryLog: TelemetryEntry[] = [];
  /** Device activity matrix keyed by siteUri. */
  deviceActivityMap = new Map<string, DeviceActivity>();
  /** Expose Map values as array for *ngFor. */
  get deviceActivities(): DeviceActivity[] {
    return Array.from(this.deviceActivityMap.values())
      .sort((a, b) => (b.lastSeen?.getTime() ?? 0) - (a.lastSeen?.getTime() ?? 0));
  }
  /** Currently selected device filter (null = all). */
  selectedDeviceFilter: string | null = null;
  get filteredLog(): TelemetryEntry[] {
    if (!this.selectedDeviceFilter) { return this.telemetryLog; }
    return this.telemetryLog.filter(e => e.deviceId === this.selectedDeviceFilter);
  }

  private static readonly MAX_LOG = 200;
  /** Seconds without a message before a device is flagged as stale. */
  private static readonly STALE_THRESHOLD_MS = 300_000;
  /** Device names (lowercase) excluded from stale detection. */
  private static readonly STALE_EXCLUDE = ['workshop camera', 'workshop_camera', 'telemetry'];

  private activityInterval: any;
  private mqttSub: Subscription | null = null;
  /** Tracks which devices have already had a stale alert played (resets when they come back online) */
  private staleAlerted = new Set<string>();

  /**
   * Configure per-camera segment counts. Keys are camera.name values.
   * Cameras not listed here default to 12 segments.
   * Example: { 'workshop_camera': 12 }
   */
  cameraSegmentCounts: { [cameraName: string]: number } = {};

  // ── Image feed state ──────────────────────────────────────────────────────

  /** Maps "${siteUri}:${segmentIndex}" → imageFeedUri populated by MQTT. */
  private segmentFeedUris = new Map<string, string>();

  activeImageFeedUri: string | null = null;
  activeImageFeedLabel = 'Image Feed';
  imageFeedImages: SmartImageDetail[] = [];
  imageFeedLoading = false;
  imageFeedPeriod = '-4h:1h';

  loadImageFeed(uri: string, period = this.imageFeedPeriod): void {
    this.activeImageFeedUri = uri;
    this.imageFeedImages = [];
    this.imageFeedLoading = true;
    
    const timeZoneOffset = this.app.getTimeZoneOffset(null);
    console.log('Loading image feed:', { uri, period, timeZoneOffset, tenantTz: this.app.getCurrentTimeZone() });
    
    this.dataService.DataGetData({
      customerId: this.app.customerId,
      request: {
        period,
        type: 'media',
        timeZoneOffset,
        forceEmbed: false,
        index: 0,
        publishedIdentifier: null,
        siteUri: null,
        timeSeries: { single: { variable: { uri, transforms: null } } }
      }
    }).subscribe(r => {
      console.log('Image feed response:', { count: r.values?.length, values: r.values });
      this.imageFeedImages = (r.values ?? []).map(i => ({ src: i.valueStr, overlay: null, timeStamp: i.timeStamp } as SmartImageDetail));
      this.imageFeedLoading = false;
    }, (err) => { 
      console.error('Image feed error:', err);
      this.imageFeedLoading = false; 
    });
  }

  onSegmentClicked(evt: { cameraUri: string; segmentIndex: number }): void {
    const key = `${evt.cameraUri}:${evt.segmentIndex}`;
    let uri = this.segmentFeedUris.get(key);

    // Fall back to camera.images[n].timeSeriesUri if MQTT hasn't seen this segment yet
    if (!uri) {
      const camera = this.fire.cameras?.find(c => (c.twin?.siteUri ?? c.siteUri) === evt.cameraUri);
      uri = camera?.images?.[evt.segmentIndex]?.timeSeriesUri;
    }

    if (!uri) { return; }

    const segCount = Object.values(this.cameraSegmentCounts)[0] ?? 12;
    const deg = 360 / segCount;
    const bearing = Math.round(evt.segmentIndex * deg);
    const cameraName = this.fire.cameras?.find(c => (c.twin?.siteUri ?? c.siteUri) === evt.cameraUri)?.name ?? evt.cameraUri;
    this.activeImageFeedLabel = `${cameraName} – ${bearing}°`;
    this.loadImageFeed(uri);
  }

  /**
   * Push a message into the feed. Call this from SignalR handlers.
   * @param deviceId   siteUri of the device
   * @param deviceName Human-readable name
   * @param topic      MQTT-style topic string, e.g. "image/detection"
   * @param payload    JSON-serialisable payload object or string
   */
  receiveTelemetry(deviceId: string, deviceName: string, topic: string, payload: any): void {
    const entry: TelemetryEntry = {
      timestamp: new Date(),
      deviceId,
      deviceName,
      topic,
      payload: typeof payload === 'string' ? payload : JSON.stringify(payload)
    };
    this.telemetryLog.unshift(entry);
    if (this.telemetryLog.length > FireForesightCommandComponent.MAX_LOG) {
      this.telemetryLog.length = FireForesightCommandComponent.MAX_LOG;
    }

    const existing = this.deviceActivityMap.get(deviceId);
    this.deviceActivityMap.set(deviceId, {
      deviceId,
      deviceName,
      lastSeen: entry.timestamp,
      lastSeenLabel: '0s ago',
      lastTopic: topic,
      offline: existing?.offline ?? false,
      stale: false
    });
  }

  private playSound(filename: string): void {
    try {
      const audio = new Audio(`assets/sounds/${filename}`);
      audio.play().catch(err => console.warn('[CommandCentre] Sound blocked:', err));
    } catch (err) {
      console.warn('[CommandCentre] Sound error:', err);
    }
  }

  secondsAgo(date: Date): string {
    const s = Math.floor((Date.now() - date.getTime()) / 1000);
    if (s < 60) { return `${s}s ago`; }
    if (s < 3600) { return `${Math.floor(s / 60)}m ago`; }
    return `${Math.floor(s / 3600)}h ago`;
  }


//   public list : StatusType[];
//   public siteId: string;
//   //public incident: Incident;
//   public liveWidget: IMultiSourceMultiValueWidget;
//   public latestImageUrl: string;
//   public selectedNote: Note;
//   public newNote: string;
  
  
  constructor(
    public app: ApplicationConfigService,
    public security: AzureAuthService,
    private svc: FireForesightService,
    public fire: FireService,
    private siteTypeService: SiteTypeService,
    private dataService: DataService,
    private toasterService: ToastrService,
    private route: ActivatedRoute,
    //public mqtt: MqttDatabusService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef) {

    this.fire.get(false);
  }

  ngOnInit(): void {
    // Run the "X ago" tick outside Angular's zone so it never triggers
    // a change detection cycle on its own; we update labels then call
    // detectChanges() once so Angular sees a stable snapshot.
    this.activityInterval = this.ngZone.runOutsideAngular(() =>
      setInterval(() => {
        const now = Date.now();
        for (const activity of this.deviceActivityMap.values()) {
          activity.lastSeenLabel = activity.lastSeen ? this.secondsAgo(activity.lastSeen) : '—';

          const excluded = FireForesightCommandComponent.STALE_EXCLUDE.includes(activity.deviceName.toLowerCase());
          if (!excluded && activity.lastSeen) {
            const age = now - activity.lastSeen.getTime();
            const isStale = age > FireForesightCommandComponent.STALE_THRESHOLD_MS;
            activity.stale = isStale;

            if (isStale && !this.staleAlerted.has(activity.deviceId)) {
              this.staleAlerted.add(activity.deviceId);
              this.ngZone.run(() => this.playSound('camera_connection_warning.mp3'));
            } else if (!isStale) {
              this.staleAlerted.delete(activity.deviceId);
            }
          }
        }
        this.cdr.detectChanges();
      }, 1000)
    );

    // ── MQTT ──────────────────────────────────────────────────────────────────
    // this.mqtt.connect();
    // this.mqttSub = this.mqtt.messages$.subscribe(msg => {
    //   this.receiveTelemetry(msg.deviceId, msg.deviceId, msg.subTopic, msg.payload);

    //   // Update the camera map marker when an image arrives
    //   try {
    //     const data = typeof msg.payload === 'string' ? JSON.parse(msg.payload) : msg.payload;
    //     if (data?.type === 'image_uploaded' && data.imageFeedUri) {
    //       const parts = (data.imageFeedUri as string).split('-');
    //       const cameraName = parts.slice(0, -1).join('-');
    //       const camera = this.fire.cameras?.find(c => c.name === cameraName);
    //       if (camera) {
    //         const siteUri = camera.twin?.siteUri ?? camera.siteUri;
    //         const segmentCount = this.cameraSegmentCounts[cameraName] ?? 12;
    //         const segmentIndex = Math.round(data.pan / (360 / segmentCount)) % segmentCount;
    //         this.commandMap?.updateCameraSegment(siteUri, segmentIndex);
    //         // Store imageFeedUri for this segment so clicks can load it
    //         this.segmentFeedUris.set(`${siteUri}:${segmentIndex}`, data.imageFeedUri);
    //         // If this feed is currently open, poll for new images after 10 seconds
    //         if (this.activeImageFeedUri === data.imageFeedUri) {
    //           setTimeout(() => {
    //             if (this.activeImageFeedUri === data.imageFeedUri) {
    //               this.loadImageFeed(data.imageFeedUri, this.imageFeedPeriod);
    //             }
    //           }, 10000);
    //         }
    //       }
    //     }
    //   } catch { /* ignore malformed payloads */ }
    // });
    // ─────────────────────────────────────────────────────────────────────────


    // Seed the activity matrix from known cameras once loaded
    const seedCameras = () => {
      if (this.fire.cameras?.length) {
        for (const cam of this.fire.cameras) {
          if (!this.deviceActivityMap.has(cam.name)) {
            this.deviceActivityMap.set(cam.name, {
              deviceId: cam.name,
              deviceName: cam.twin?.siteName ?? cam.name,
              lastSeen: null,
              lastSeenLabel: '—',
              lastTopic: '—',
              offline: cam.offline ?? false,
              stale: false
            });
          }
        }
      } else {
        setTimeout(seedCameras, 800);
      }
    };
    setTimeout(seedCameras, 800);
  }

  ngOnDestroy(): void {
    clearInterval(this.activityInterval);
    // this.mqttSub?.unsubscribe();
    // this.mqtt.disconnect();
  }

  ngAfterViewInit(): void {
    // Wait 2 seconds after view init, then toggle cameras and incidents on
    setTimeout(() => {
      if (this.commandMap) {
        this.toggleCameras();
        this.toggleIncidents();
      }
    }, 5000);
  }

  onMapReady(map: L.Map): void {
    this.leafletMap = map;
  }

  /** Called when the incident feed polls the cloud and gets a refreshed incident */
  onIncidentRefreshed(incident: Incident): void {
    if (this.commandMap && this.incidentsSelected) {
      this.commandMap.updateIncidentMarker(incident);
    }
  }
  layersSelected = false;
  camerasSelected = false;
  incidentsSelected = false;

  toggleIncidents(): void {
    if (!this.incidentsSelected) {
      this.incidentsSelected = true;
      if (!this.fire.incidents?.length) {
        this.fire.getIncidents().then(() => {
          this.commandMap.displayIncidents(this.fire.incidents);
        });
      } else {
        this.commandMap.displayIncidents(this.fire.incidents);
      }
    } else {
      this.incidentsSelected = false;
      this.commandMap.clearIncidents();
    }
  }

  toggleCameras(): void {
    if (!this.camerasSelected) {
      this.camerasSelected = true;
      // Ensure cameras are loaded first
      if (!this.fire.cameras?.length) {
        this.fire.get(false);
        // Wait briefly for cameras to load, then render
        const waitForCameras = () => {
          if (this.fire.cameras?.length) {
            this.commandMap.displayCameras(this.fire.cameras);
          } else {
            setTimeout(waitForCameras, 500);
          }
        };
        setTimeout(waitForCameras, 500);
      } else {
        this.commandMap.displayCameras(this.fire.cameras);
      }
    } else {
      this.camerasSelected = false;
      this.commandMap.clearCameras();
    }
  }

  toggleLayers(): void {
    if (!this.layersSelected) {
      if (!this.fire.layers) {
        this.svc.FireForesightGetMapLayers(this.app.customerId).subscribe(layers => {
          this.fire.layers = layers;
          if (this.fire.layers) { this.toggleLayers(); }
        });
        return;
      }
      this.layersSelected = true;
      for (const layer of this.fire.layers) {
        if ((layer as any).layer != null) { continue; }
        if (layer.layerTypeId === 1) {
          this.dataService.DataProxy(layer.url).subscribe(resp => {
            (layer as any).layer = this.commandMap.loadLinkLayerContent(
              'geojson', layer.mapLayerId, JSON.parse(resp), layer.fileLayerName, layer.styles, layer.tileSize, layer.useProxy, null);
          });
        } else if (layer.layerTypeId === 2) {
          if (layer.url.slice(-3).toLowerCase() === 'kmz') {
            (layer as any).layer = this.commandMap.loadLinkLayerContent(
              'kmz', layer.mapLayerId, layer.url, layer.fileLayerName, layer.styles, layer.tileSize, layer.useProxy, null);
          } else {
            this.dataService.DataProxy(layer.url).subscribe(resp => {
              const parser = new DOMParser();
              const kml = parser.parseFromString(resp, 'text/xml');
              const hrefEls = kml.getElementsByTagName('href');
              for (let i = 0; i < hrefEls.length; i++) {
                const val = (hrefEls[i].textContent || '').trim();
                if (val && !val.startsWith('http') && !val.startsWith('data:') && !val.startsWith('#')) {
                  try { hrefEls[i].textContent = new URL(val, layer.url).href; } catch {}
                }
              }
              (layer as any).layer = this.commandMap.loadLinkLayerContent(
                'kml', layer.mapLayerId, kml, layer.fileLayerName, layer.styles, layer.tileSize, layer.useProxy, null);
            });
          }
        } else if (layer.layerTypeId === 3) {
          (layer as any).layer = this.commandMap.loadLinkLayerContent(
            'esri', layer.mapLayerId, layer.url, layer.fileLayerName, layer.styles, layer.tileSize, layer.useProxy, null);
        } else if (layer.layerTypeId === 4) {
          this.dataService.DataProxy(layer.url).subscribe(resp => {
            (layer as any).layer = this.commandMap.loadLinkLayerContent(
              'cap', layer.mapLayerId, JSON.parse(resp), layer.fileLayerName, layer.styles, layer.tileSize, layer.useProxy, null);
          });
        } else if (layer.layerTypeId === 5) {
          (layer as any).layer = this.commandMap.loadLinkLayerContent(
            'wms', layer.mapLayerId, layer.url, layer.fileLayerName, layer.styles, layer.tileSize, layer.useProxy, null);
        } else if (layer.layerTypeId === 7) {
          (layer as any).layer = this.commandMap.loadLinkLayerContent(
            'tilemap', layer.mapLayerId, layer.url, layer.fileLayerName, layer.styles, layer.tileSize, layer.useProxy, null);
        } else if (layer.layerTypeId === 8 && layer.url.slice(-3).toLowerCase() !== 'dat') {
          this.dataService.DataProxy(layer.url).subscribe(resp => {
            (layer as any).layer = this.commandMap.loadLinkLayerContent(
              'waemergency', layer.mapLayerId, JSON.parse(resp), layer.fileLayerName, layer.styles, layer.tileSize, layer.useProxy, null);
          });
        }
      }
    } else {
      this.layersSelected = false;
      if (this.fire.layers) {
        for (const layer of this.fire.layers) {
          if ((layer as any).layer) {
            this.commandMap.removeLayer((layer as any).layer);
            (layer as any).layer = null;
          }
        }
      }
    }
  }


  
}
